import { Award, Calendar, Clock, Heart, MessageSquare, Shield, Target, TrendingUp, Users, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  const stats = [
    { number: '50+', label: 'Businesses Served' },
    { number: '10,000+', label: 'Bookings Automated' },
    { number: '99.9%', label: 'Uptime Guarantee' },
    { number: '95%', label: 'Customer Satisfaction' }
  ];

  const values = [
    {
      icon: Heart,
      title: 'Customer First',
      description: 'Every feature we build starts with understanding your needs and your customers\' experience.'
    },
    {
      icon: Shield,
      title: 'Trust & Security',
      description: 'Your business data and customer information are protected with enterprise-grade security.'
    },
    {
      icon: Zap,
      title: 'Continuous Innovation',
      description: 'We constantly improve our AI based on real-world usage and customer feedback.'
    },
    {
      icon: Users,
      title: 'Partnership Approach',
      description: 'We\'re not just a software provider - we\'re your partner in business growth.'
    }
  ];

  const timeline = [
    {
      year: '2024',
      title: 'The Vision',
      description: 'Recognizing that small businesses lose thousands monthly to missed calls and booking inefficiencies, we set out to democratize AI technology for appointment-based businesses.'
    },
    {
      year: '2024',
      title: 'Product Development',
      description: 'Built and refined our AI assistant through extensive testing with real salons, barbershops, and spas to ensure it understands the nuances of service-based businesses.'
    },
    {
      year: '2025',
      title: 'Market Launch',
      description: 'Launched Booker.Chat with WhatsApp and voice capabilities, helping businesses automate their booking process and focus on what they do best - serving customers.'
    },
    {
      year: 'Future',
      title: 'Expansion',
      description: 'Expanding to serve more business types and adding advanced features like predictive scheduling and customer lifetime value optimization.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-blue-600" />
              <span className="ml-2 text-xl font-bold text-gray-900">Booker.Chat</span>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                <Link to="/mission" className="text-gray-500 hover:text-gray-900 px-3 py-2 text-sm font-medium">Our Mission</Link>
                <Link to="/team" className="text-gray-500 hover:text-gray-900 px-3 py-2 text-sm font-medium">Team</Link>
                <Link to="/values" className="text-gray-500 hover:text-gray-900 px-3 py-2 text-sm font-medium">Values</Link>
                <button className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
                  Start Free Trial
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
            Empowering Small Businesses with
            <span className="text-blue-600"> AI Technology</span>
          </h1>
          <p className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto">
            We believe every business deserves access to cutting-edge AI technology. Booker.Chat makes enterprise-level automation affordable and accessible for appointment-based businesses of all sizes.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index}>
                <div className="text-4xl font-bold text-blue-600">{stat.number}</div>
                <div className="mt-2 text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section id="mission" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
                Our Mission
              </h2>
              <p className="mt-4 text-lg text-gray-600">
                Small businesses are the backbone of our communities, yet they often lack access to the same technological advantages as large corporations. We're changing that.
              </p>
              <p className="mt-4 text-lg text-gray-600">
                Booker.Chat was founded on the belief that every salon, barbershop, spa, and service business should have access to AI-powered automation that helps them grow, serve customers better, and focus on their craft instead of administrative tasks.
              </p>
              <div className="mt-8 space-y-4">
                <div className="flex items-start">
                  <Target className="h-6 w-6 text-blue-600 mt-1 mr-4" />
                  <div>
                    <h3 className="font-medium text-gray-900">Democratize AI Technology</h3>
                    <p className="text-gray-600">Make advanced AI accessible to businesses of all sizes</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <TrendingUp className="h-6 w-6 text-blue-600 mt-1 mr-4" />
                  <div>
                    <h3 className="font-medium text-gray-900">Drive Business Growth</h3>
                    <p className="text-gray-600">Help businesses capture more revenue and serve customers better</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Clock className="h-6 w-6 text-blue-600 mt-1 mr-4" />
                  <div>
                    <h3 className="font-medium text-gray-900">Save Valuable Time</h3>
                    <p className="text-gray-600">Automate routine tasks so business owners can focus on what matters</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 lg:mt-0">
              <div className="bg-blue-600 rounded-lg p-8 text-white">
                <blockquote className="text-xl font-medium">
                  "We started Booker.Chat because we saw too many talented business owners spending their days answering phones instead of serving customers. AI should work for everyone, not just tech giants."
                </blockquote>
                <div className="mt-6">
                  <div className="font-medium">Sarah Chen</div>
                  <div className="text-blue-200">Founder & CEO</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">The Problem We're Solving</h2>
            <p className="mt-4 text-lg text-gray-600">
              Small businesses face unique challenges that hurt their growth and customer experience
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                <MessageSquare className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-4">Missed Revenue</h3>
              <p className="text-gray-600">
                The average salon loses $2,400+ monthly from missed calls, especially during busy periods and after hours.
              </p>
            </div>
            
            <div className="text-center">
              <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                <Clock className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-4">Time Waste</h3>
              <p className="text-gray-600">
                Business owners spend hours daily on booking calls instead of focusing on their customers and growing their business.
              </p>
            </div>
            
            <div className="text-center">
              <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                <Users className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-4">Poor Customer Experience</h3>
              <p className="text-gray-600">
                Customers get frustrated with busy signals, voicemails, and delayed responses, often choosing competitors instead.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section id="values" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Our Values</h2>
            <p className="mt-4 text-lg text-gray-600">
              The principles that guide everything we do
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow">
                <div className="flex items-center mb-4">
                  <value.icon className="h-8 w-8 text-blue-600 mr-4" />
                  <h3 className="text-xl font-medium text-gray-900">{value.title}</h3>
                </div>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Journey */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Our Journey</h2>
            <p className="mt-4 text-lg text-gray-600">
              From idea to helping businesses grow
            </p>
          </div>
          
          <div className="space-y-8">
            {timeline.map((item, index) => (
              <div key={index} className="flex items-start">
                <div className="flex-shrink-0 w-20 text-right">
                  <span className="text-lg font-bold text-blue-600">{item.year}</span>
                </div>
                <div className="ml-8 flex-grow">
                  <h3 className="text-xl font-medium text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
            <div>
              <h2 className="text-3xl font-bold text-white sm:text-4xl">
                Built with Cutting-Edge Technology
              </h2>
              <p className="mt-4 text-xl text-blue-100">
                Our AI assistant combines the latest advances in natural language processing, voice recognition, and machine learning to deliver human-like interactions.
              </p>
              <div className="mt-8 space-y-4">
                <div className="flex items-center">
                  <Award className="h-6 w-6 text-blue-300 mr-4" />
                  <span className="text-blue-100">Enterprise-grade security and reliability</span>
                </div>
                <div className="flex items-center">
                  <Shield className="h-6 w-6 text-blue-300 mr-4" />
                  <span className="text-blue-100">SOC 2 Type II certified infrastructure</span>
                </div>
                <div className="flex items-center">
                  <Zap className="h-6 w-6 text-blue-300 mr-4" />
                  <span className="text-blue-100">99.9% uptime with automatic failovers</span>
                </div>
              </div>
            </div>
            <div className="mt-8 lg:mt-0">
              <div className="bg-white rounded-lg p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Technology Stack</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">AI & ML</h4>
                    <ul className="space-y-1 text-gray-600">
                      <li>• Advanced NLP</li>
                      <li>• Voice Recognition</li>
                      <li>• Machine Learning</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Infrastructure</h4>
                    <ul className="space-y-1 text-gray-600">
                      <li>• Cloud-Native</li>
                      <li>• Auto-Scaling</li>
                      <li>• Real-time Sync</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Integrations</h4>
                    <ul className="space-y-1 text-gray-600">
                      <li>• WhatsApp Business API</li>
                      <li>• Google Calendar</li>
                      <li>• Stripe Payments</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Security</h4>
                    <ul className="space-y-1 text-gray-600">
                      <li>• End-to-End Encryption</li>
                      <li>• GDPR Compliant</li>
                      <li>• SOC 2 Certified</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Leadership Team</h2>
            <p className="mt-4 text-lg text-gray-600">
              Experienced professionals passionate about helping small businesses thrive
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="mx-auto h-32 w-32 bg-gray-300 rounded-full mb-6"></div>
              <h3 className="text-xl font-medium text-gray-900">Sarah Chen</h3>
              <p className="text-blue-600 mb-3">Founder & CEO</p>
              <p className="text-gray-600 text-sm">
                Former tech executive with 15+ years experience building AI products. Previously led AI initiatives at major tech companies.
              </p>
            </div>
            
            <div className="text-center">
              <div className="mx-auto h-32 w-32 bg-gray-300 rounded-full mb-6"></div>
              <h3 className="text-xl font-medium text-gray-900">Marcus Rodriguez</h3>
              <p className="text-blue-600 mb-3">CTO</p>
              <p className="text-gray-600 text-sm">
                AI researcher and engineer specializing in natural language processing and voice technology. PhD in Computer Science.
              </p>
            </div>
            
            <div className="text-center">
              <div className="mx-auto h-32 w-32 bg-gray-300 rounded-full mb-6"></div>
              <h3 className="text-xl font-medium text-gray-900">Lisa Thompson</h3>
              <p className="text-blue-600 mb-3">VP of Customer Success</p>
              <p className="text-gray-600 text-sm">
                Small business expert with deep understanding of service industry operations and customer experience optimization.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900">Ready to Join Our Mission?</h2>
          <p className="mt-4 text-xl text-gray-600">
            Let's work together to transform your business with AI technology
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700">
              Start Your Free Trial
            </button>
            <button className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg text-lg font-medium hover:border-gray-400">
              Schedule a Demo
            </button>
          </div>
          <p className="mt-4 text-gray-500">
            Join the hundreds of businesses already growing with Booker.Chat
          </p>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;